import { createSlice } from '@reduxjs/toolkit';

// 初期型
export interface JobRecommendOffsetState {
  // requirements 募集・採用情報
  inexperiencedOccupationWelcome: number | null;
  industryInexperiencedWelcome: number | null;
  noAcademicBackgroundRequired: number | null;
  noTransfer: number | null;
  secondTimeGraduate: number | null;
  expert: number | null;
  urgentRecruit: number | null;
  employsManyPeople: number | null;
  noChangeInJobTitle: number | null;

  // income 年収
  annualIncome300to400: number | null;
  annualIncome400to500: number | null;
  annualIncome500to600: number | null;
  annualIncome600to700: number | null;
  annualIncome700to800: number | null;
  annualIncome800to900: number | null;
  annualIncome900to1000: number | null;
  annualIncomeMoreThan1000: number | null;

  // workplaceEnvironment 会社・職場の環境
  listedCompany: number | null;
  foreignCompany: number | null;
  fewerRetirees: number | null;
  femaleThriving: number | null;
  commutingByCar: number | null;
  freeDressCode: number | null;

  // holidayAndWayOfWorking 休日・働き方
  fiveDayWorkweekSystem: number | null;
  MoreThan120DaysOffAYear: number | null;
  holiday: number | null;
  consecutiveVacations: number | null;
  noOvertime: number | null;
  littleOvertime: number | null;
  flexibleWork: number | null;
  maternityAndChildcareLeave: number | null;

  // employeeAverageAge 社員の平均年齢
  twenties: number | null;
  thirties: number | null;
  forties: number | null;
  overFifties: number | null;

  // employmentStatus 雇用形態
  regularEmployee: number | null;
  contractEmployee: number | null;
  other: number | null;

  // employmentStatus 待遇・福利厚生
  retirementBenefitPlan: number | null;
  companyHousingAndEentSubsidySystem: number | null;
  childcareAndChildcareSupportSystem: number | null;
  qualificationAcquisitionSupportSystem: number | null;
  fullTrainingSystem: number | null;
  salaryOf250kYenMore: number | null;
  salaryOf350kYenMore: number | null;
  UIturnSupport: number | null;

  // positionsAndLanguageSkills 仕事内容・語学
  overseasBusinessTrip: number | null;
  manager: number | null;
  english: number | null;
  chinese: number | null;

  // establish 設立年数
  establish1Year: number | null;
  establish10Year: number | null;
  establish50Year: number | null;
  establishMoreThan50Year: number | null;
}

// 初期状態
export const jobRecommendOffsetInitialState: JobRecommendOffsetState = {
  // requirements 募集・採用情報
  inexperiencedOccupationWelcome: null,
  industryInexperiencedWelcome: null,
  noAcademicBackgroundRequired: null,
  noTransfer: null,
  secondTimeGraduate: null,
  expert: null,
  urgentRecruit: null,
  employsManyPeople: null,
  noChangeInJobTitle: null,

  // income 年収
  annualIncome300to400: null,
  annualIncome400to500: null,
  annualIncome500to600: null,
  annualIncome600to700: null,
  annualIncome700to800: null,
  annualIncome800to900: null,
  annualIncome900to1000: null,
  annualIncomeMoreThan1000: null,

  // workplaceEnvironment 会社・職場の環境
  listedCompany: null,
  foreignCompany: null,
  fewerRetirees: null,
  femaleThriving: null,
  commutingByCar: null,
  freeDressCode: null,

  // holidayAndWayOfWorking 休日・働き方
  fiveDayWorkweekSystem: null,
  MoreThan120DaysOffAYear: null,
  holiday: null,
  consecutiveVacations: null,
  noOvertime: null,
  littleOvertime: null,
  flexibleWork: null,
  maternityAndChildcareLeave: null,

  // employeeAverageAge 社員の平均年齢
  twenties: null,
  thirties: null,
  forties: null,
  overFifties: null,

  // employmentStatus 雇用形態
  regularEmployee: null,
  contractEmployee: null,
  other: null,

  // employmentStatus 待遇・福利厚生
  retirementBenefitPlan: null,
  companyHousingAndEentSubsidySystem: null,
  childcareAndChildcareSupportSystem: null,
  qualificationAcquisitionSupportSystem: null,
  fullTrainingSystem: null,
  salaryOf250kYenMore: null,
  salaryOf350kYenMore: null,
  UIturnSupport: null,

  // positionsAndLanguageSkills 仕事内容・語学
  overseasBusinessTrip: null,
  manager: null,
  english: null,
  chinese: null,

  // establish 設立年数
  establish1Year: null,
  establish10Year: null,
  establish50Year: null,
  establishMoreThan50Year: null,
};

// State型
export interface JobTrendRecommendOffsetSliceState {
  jobTrendRecommendList: ReturnType<typeof jobRecommendOffsetReducer>;
}

const jobRecommendOffsetSlice = createSlice({
  name: 'jobRecommendOffset',
  initialState: jobRecommendOffsetInitialState,
  reducers: {
    setInexperiencedOccupationWelcome: (state, action) => {
      state.inexperiencedOccupationWelcome = action.payload;
    },
    setIndustryInexperiencedWelcome: (state, action) => {
      state.industryInexperiencedWelcome = action.payload;
    },
    setNoAcademicBackgroundRequired: (state, action) => {
      state.noAcademicBackgroundRequired = action.payload;
    },
    setNoTransfer: (state, action) => {
      state.noTransfer = action.payload;
    },
    setSecondTimeGraduate: (state, action) => {
      state.secondTimeGraduate = action.payload;
    },
    setExpert: (state, action) => {
      state.expert = action.payload;
    },
    setUrgentRecruit: (state, action) => {
      state.urgentRecruit = action.payload;
    },
    setEmploysManyPeople: (state, action) => {
      state.employsManyPeople = action.payload;
    },
    setNoChangeInJobTitle: (state, action) => {
      state.noChangeInJobTitle = action.payload;
    },

    setAnnualIncome300to400: (state, action) => {
      state.annualIncome300to400 = action.payload;
    },
    setAnnualIncome400to500: (state, action) => {
      state.annualIncome400to500 = action.payload;
    },
    setAnnualIncome500to600: (state, action) => {
      state.annualIncome500to600 = action.payload;
    },
    setAnnualIncome600to700: (state, action) => {
      state.annualIncome600to700 = action.payload;
    },
    setAnnualIncome700to800: (state, action) => {
      state.annualIncome700to800 = action.payload;
    },
    setAnnualIncome800to900: (state, action) => {
      state.annualIncome800to900 = action.payload;
    },
    setAnnualIncome900to1000: (state, action) => {
      state.annualIncome900to1000 = action.payload;
    },
    setAnnualIncomeMoreThan1000: (state, action) => {
      state.annualIncomeMoreThan1000 = action.payload;
    },

    setListedCompany: (state, action) => {
      state.listedCompany = action.payload;
    },
    setForeignCompany: (state, action) => {
      state.foreignCompany = action.payload;
    },
    setFewerRetirees: (state, action) => {
      state.fewerRetirees = action.payload;
    },
    setFemaleThriving: (state, action) => {
      state.femaleThriving = action.payload;
    },
    setCommutingByCar: (state, action) => {
      state.commutingByCar = action.payload;
    },
    setFreeDressCode: (state, action) => {
      state.freeDressCode = action.payload;
    },

    setFiveDayWorkweekSystem: (state, action) => {
      state.fiveDayWorkweekSystem = action.payload;
    },
    setMoreThan120DaysOffAYear: (state, action) => {
      state.MoreThan120DaysOffAYear = action.payload;
    },
    setHoliday: (state, action) => {
      state.holiday = action.payload;
    },
    setConsecutiveVacations: (state, action) => {
      state.consecutiveVacations = action.payload;
    },
    setNoOvertime: (state, action) => {
      state.noOvertime = action.payload;
    },
    setLittleOvertime: (state, action) => {
      state.littleOvertime = action.payload;
    },
    setFlexibleWork: (state, action) => {
      state.flexibleWork = action.payload;
    },
    setMaternityAndChildcareLeave: (state, action) => {
      state.maternityAndChildcareLeave = action.payload;
    },

    setTwenties: (state, action) => {
      state.twenties = action.payload;
    },
    setThirties: (state, action) => {
      state.thirties = action.payload;
    },
    setForties: (state, action) => {
      state.forties = action.payload;
    },
    setOverFifties: (state, action) => {
      state.overFifties = action.payload;
    },

    setRegularEmployee: (state, action) => {
      state.regularEmployee = action.payload;
    },
    setContractEmployee: (state, action) => {
      state.contractEmployee = action.payload;
    },
    setOther: (state, action) => {
      state.other = action.payload;
    },

    setRetirementBenefitPlan: (state, action) => {
      state.retirementBenefitPlan = action.payload;
    },
    setCompanyHousingAndEentSubsidySystem: (state, action) => {
      state.companyHousingAndEentSubsidySystem = action.payload;
    },
    setChildcareAndChildcareSupportSystem: (state, action) => {
      state.childcareAndChildcareSupportSystem = action.payload;
    },
    setQualificationAcquisitionSupportSystem: (state, action) => {
      state.qualificationAcquisitionSupportSystem = action.payload;
    },
    setFullTrainingSystem: (state, action) => {
      state.fullTrainingSystem = action.payload;
    },
    setSalaryOf250kYenMore: (state, action) => {
      state.salaryOf250kYenMore = action.payload;
    },
    setSalaryOf350kYenMore: (state, action) => {
      state.salaryOf350kYenMore = action.payload;
    },
    setUIturnSupport: (state, action) => {
      state.UIturnSupport = action.payload;
    },

    setOverseasBusinessTrip: (state, action) => {
      state.overseasBusinessTrip = action.payload;
    },
    setManager: (state, action) => {
      state.manager = action.payload;
    },
    setEnglish: (state, action) => {
      state.english = action.payload;
    },
    setChinese: (state, action) => {
      state.chinese = action.payload;
    },

    setEstablish1Year: (state, action) => {
      state.establish1Year = action.payload;
    },
    setEstablish10Year: (state, action) => {
      state.establish10Year = action.payload;
    },
    setEstablish50Year: (state, action) => {
      state.establish50Year = action.payload;
    },
    setEstablishMoreThan50Year: (state, action) => {
      state.establishMoreThan50Year = action.payload;
    },
  },
});

// Create Action
export const {
  setInexperiencedOccupationWelcome,
  setIndustryInexperiencedWelcome,
  setNoAcademicBackgroundRequired,
  setNoTransfer,
  setSecondTimeGraduate,
  setExpert,
  setUrgentRecruit,
  setEmploysManyPeople,
  setNoChangeInJobTitle,
  setAnnualIncome300to400,
  setAnnualIncome400to500,
  setAnnualIncome500to600,
  setAnnualIncome600to700,
  setAnnualIncome700to800,
  setAnnualIncome800to900,
  setAnnualIncome900to1000,
  setAnnualIncomeMoreThan1000,
  setListedCompany,
  setForeignCompany,
  setFewerRetirees,
  setFemaleThriving,
  setCommutingByCar,
  setFreeDressCode,
  setFiveDayWorkweekSystem,
  setMoreThan120DaysOffAYear,
  setHoliday,
  setConsecutiveVacations,
  setNoOvertime,
  setLittleOvertime,
  setFlexibleWork,
  setMaternityAndChildcareLeave,
  setTwenties,
  setThirties,
  setForties,
  setOverFifties,
  setRegularEmployee,
  setContractEmployee,
  setOther,
  setRetirementBenefitPlan,
  setCompanyHousingAndEentSubsidySystem,
  setChildcareAndChildcareSupportSystem,
  setQualificationAcquisitionSupportSystem,
  setFullTrainingSystem,
  setSalaryOf250kYenMore,
  setSalaryOf350kYenMore,
  setUIturnSupport,
  setOverseasBusinessTrip,
  setManager,
  setEnglish,
  setChinese,
  setEstablish1Year,
  setEstablish10Year,
  setEstablish50Year,
  setEstablishMoreThan50Year,
} = jobRecommendOffsetSlice.actions;

// reducer
export const jobRecommendOffsetReducer = jobRecommendOffsetSlice.reducer;

// selector
export const jobRecommendOffsetSelector = (state) => state.jobRecommendOffset;
